import { gql } from '@apollo/client';

/**
 * Writing an asset model.
 *
 * Three calls, in order, because each needs what the one before it returns:
 * create the model, optionally attach the funds that implement it, then sync —
 * which is what fills the implementation in when no funds were named by hand.
 *
 * Every weight is a FRACTION here (0.6, not 60). The form takes percentages
 * because that is how an allocation is spoken, and converts on the way out.
 */
export const CREATE_ASSET_MODEL = gql`
  mutation CreateAssetModel(
    $name: String!
    $allocations: [AssetAllocationInput!]!
    $source: String
    $description: String
    $language: String
    $riskPreference: RiskProfile
    $timeHorizon: TimeHorizon
    $category: AssetModelCategory
    $currency: CurrencyEnum! = INR
  ) {
    createAssetModel(
      name: $name
      allocations: $allocations
      source: $source
      description: $description
      language: $language
      riskPreference: $riskPreference
      timeHorizon: $timeHorizon
      category: $category
      currency: $currency
    ) {
      id
      name
      modelStandardName
      description
      source
      currency
      riskPreference
      timeHorizon
      category
      isActive
      isListable
      iconUrl
      allocations {
        id
        assetClassId
        weight
        toleranceBand
        minCapital
        rebalancingFrequency
      }
    }
  }
`;

/** Fills in the funds for a model whose implementation was left to the engine. */
/**
 * `includeEtfs` is non-null on the resolver — a Python default of `False`
 * does not make a Strawberry argument nullable, only `Optional[...]` does —
 * so the variable is declared `Boolean!` here and every caller must always
 * send a real `true`/`false`, never omit it or pass `null`.
 */
export const SYNC_ASSET_MODEL = gql`
  mutation SyncOneModel($modelId: Int!, $includeEtfs: Boolean!) {
    syncAssetModel(modelId: $modelId, includeEtfs: $includeEtfs) {
      success
      message
    }
  }
`;

/**
 * Adds, swaps or drops the funds that implement each SEBI slice of a model,
 * per variant (`variantIndex` is the variant's 0-based id — 0 is "Best").
 *
 * A fund is keyed on (assetClassId, sebiCategoryId, securitySymbol); one sent
 * with `replacesSymbol` takes the named fund's place in its slot, and
 * `removeFunds` drops funds outright. Used by the create form (funds named per
 * slice) and by Edit constituents.
 */
export const UPSERT_ASSET_MODEL_FUNDS = gql`
  mutation UpsertAssetModelFunds($input: UpsertAssetModelFundsInput!) {
    upsertAssetModelFunds(input: $input) {
      success
      message
      addedCount
      updatedCount
      removedCount
      implementations {
        id
        modelId
        assetClassId
        sebiCategoryId
        holdingSecuritySymbol
        securityName
        weightWithinSebiCategory
        weightWithinAssetClass
        weightWithinAssetModel
        rebalancingFrequency
        isActive
      }
    }
  }
`;

/**
 * Upload an image as a model's icon (KI staff, from Manage model).
 *
 * The file travels as a multipart upload — apollo-upload-client, already the
 * app's terminating link. The server stores it on S3 as `<modelId>.png`,
 * overwriting the previous icon, and writes the URL (with a `?v=` cache-buster)
 * to the model's `iconUrl`. It checks neither type nor size, which is why the
 * app converts the picked image to a bounded PNG before sending it.
 */
export const UPLOAD_ASSET_MODEL_ICON = gql`
  mutation UploadAssetModelIcon($modelId: Int!, $file: Upload!) {
    uploadAssetModelIcon(modelId: $modelId, file: $file) {
      success
      message
      modelId
    }
  }
`;
